import { createContext, createElement, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { CapabilityMap } from "@/lib/admin-permissions";

export type AdminRole = "owner" | "admin" | "viewer";
export type AdminStatus = "active" | "disabled";

export interface AdminSession {
  loading: boolean;
  authed: boolean;
  email: string | null;
  role: AdminRole | null;
  status: AdminStatus | null;
  caps: CapabilityMap | null;
  error: "unauthorized" | "forbidden" | "disabled" | "network" | null;
}

const initial: AdminSession = {
  loading: true,
  authed: false,
  email: null,
  role: null,
  status: null,
  caps: null,
  error: null,
};

async function fetchMe(token: string): Promise<Partial<AdminSession>> {
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-me`;
  try {
    const r = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
    });
    if (r.status === 401) return { error: "unauthorized" };
    if (r.status === 403) {
      const body = await r.json().catch(() => ({}));
      return {
        error: body?.error === "Account disabled" ? "disabled" : "forbidden",
      };
    }
    if (!r.ok) return { error: "network" };
    const data = await r.json();
    return {
      email: data.email,
      role: data.role,
      status: data.status,
      caps: data.capabilities ?? null,
      error: null,
    };
  } catch {
    return { error: "network" };
  }
}

const AdminSessionContext = createContext<AdminSession | null>(null);

export function AdminSessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AdminSession>(initial);
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const evaluate = async () => {
      const { data } = await supabase.auth.getSession();
      let session = data.session;
      if (!session) {
        lastUserIdRef.current = null;
        if (mounted) setState({ ...initial, loading: false });
        return;
      }
      let me = await fetchMe(session.access_token);
      if (me.error === "unauthorized") {
        const { data: refreshed } = await supabase.auth.refreshSession();
        if (refreshed.session?.access_token) {
          session = refreshed.session;
          me = await fetchMe(session.access_token);
        }
      }
      if (!mounted) return;
      lastUserIdRef.current = session.user.id;
      setState({
        loading: false,
        authed: true,
        email: me.email ?? session.user.email ?? null,
        role: (me.role as AdminRole) ?? null,
        status: (me.status as AdminStatus) ?? null,
        caps: me.caps ?? null,
        error: me.error ?? null,
      });
    };

    evaluate();

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      // Ignore silent token refreshes and metadata updates — they don't
      // change the admin identity and trigger expensive admin-me re-fetches.
      if (event === "TOKEN_REFRESHED" || event === "USER_UPDATED") return;
      // Only re-evaluate if the actual user changed (or signed in/out).
      const nextUserId = session?.user?.id ?? null;
      if (event === "INITIAL_SESSION" && nextUserId === lastUserIdRef.current) return;
      evaluate();
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return createElement(AdminSessionContext.Provider, { value: state }, children);
}

export function useAdminSession(): AdminSession {
  const ctx = useContext(AdminSessionContext);
  if (ctx) return ctx;
  // Fallback for any consumer rendered outside the provider (e.g. the login
  // page when not wrapped). Preserves prior behaviour.
  return useStandaloneAdminSession();
}

function useStandaloneAdminSession(): AdminSession {
  const [state, setState] = useState<AdminSession>(initial);

  useEffect(() => {
    let mounted = true;
    const evaluate = async () => {
      const { data } = await supabase.auth.getSession();
      let session = data.session;
      if (!session) {
        if (mounted) setState({ ...initial, loading: false });
        return;
      }
      let me = await fetchMe(session.access_token);
      if (me.error === "unauthorized") {
        const { data: refreshed } = await supabase.auth.refreshSession();
        if (refreshed.session?.access_token) {
          session = refreshed.session;
          me = await fetchMe(session.access_token);
        }
      }
      if (!mounted) return;
      setState({
        loading: false,
        authed: true,
        email: me.email ?? session.user.email ?? null,
        role: (me.role as AdminRole) ?? null,
        status: (me.status as AdminStatus) ?? null,
        caps: me.caps ?? null,
        error: me.error ?? null,
      });
    };
    evaluate();
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "TOKEN_REFRESHED" || event === "USER_UPDATED") return;
      evaluate();
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}
